import type { CallQualityView, TimelineRecording } from "@gioia365/contracts";
import { AUTOMATIC_OUTCOME_DETAIL } from "@gioia365/domain/call-outcomes";
import type { MessageKey } from "@gioia365/i18n";
import type { StatusPill } from "../design/components/StatusPill";
import type { Tone } from "../design/components/tone";
import type { Translate } from "../i18n/locale";
import { outcomeLabel } from "../leads/lead-display";

// Come si dice una telefonata, uguale nella Timeline del Lead e in «Conversazioni» (piano M4, Task 12 e Task 19; D14: è lo
// stesso evento): l'esito in due passi con il dettaglio, la riga della registrazione con le parole e i toni di `REC_TEXT` del
// mock-up, e la rilettura da sola mentre una registrazione è in preparazione (minore di R2 per R3).

/**
 * Il dettaglio di «Nessuna risposta» con le parole della postazione: le `disposition` di Close, «Richiama ora» della telefonia
 * (`call_again_now`, piano M4, Task 10) e «segnata da Gioia365» per l'esito che Gioia365 dà da sé (TEL-32).
 */
const CALL_DETAILS: Readonly<Record<string, MessageKey>> = {
  busy: "timeline.call.details.busy",
  "vm-answer": "timeline.call.details.vmAnswer",
  "vm-left": "timeline.call.details.vmLeft",
  abandoned: "timeline.call.details.abandoned",
  blocked: "timeline.call.details.blocked",
  call_again_now: "callDesk.callAgainNow",
  [AUTOMATIC_OUTCOME_DETAIL]: "timeline.call.details.automatic",
};

/** L'esito con il secondo passo dopo «Risposta» («Risposta · Richiamare») e il dettaglio, se c'è. */
export function outcomeText(t: Translate, outcome: string, answeredOutcome: string | null | undefined, detail: string | null): string {
  const label =
    outcome === "answered" && answeredOutcome
      ? t("timeline.call.twoSteps", { outcome: outcomeLabel(t, outcome), answered: outcomeLabel(t, answeredOutcome) })
      : outcomeLabel(t, outcome);
  const detailKey = detail === null ? undefined : CALL_DETAILS[detail];
  return detailKey ? t("timeline.call.outcomeWithDetail", { outcome: label, detail: t(detailKey) }) : label;
}

type StatusIcon = Parameters<typeof StatusPill>[0]["icon"];

export function callQualityLabel(t: Translate, quality: CallQualityView | null | undefined): string | null {
  if (quality?.closureQuality === "closed_early") return t("callDesk.earlyLabel");
  if (quality?.closureQuality === "hangup_unmeasured") return t("callDesk.unmeasuredLabel");
  if (quality?.numberProblem === "unreachable") return t("callDesk.unreachableLabel");
  return quality?.numberProblem ? t("callDesk.invalidNumberLabel") : null;
}

export function ringDurationText(t: Translate, quality: CallQualityView | null | undefined): string | null {
  if (!quality) return null;
  return quality.ringDurationSeconds === null ? t("callDesk.ringDurationUnknown") : t("callDesk.ringDuration", { seconds: quality.ringDurationSeconds });
}

/** Lo stato da mostrare di una registrazione, lo stesso nella Timeline e in «Conversazioni». */
type RecordingState = TimelineRecording["state"];

/**
 * Le righe della registrazione, con il testo e il tono di `REC_TEXT` del mock-up (`success`, `warning`, `info` → `accent`,
 * `muted` → `neutral`, `danger`) e un'icona, perché il colore non basta. «Rimasta in Close» (se c'era) e «inserita a mano» sono la
 * registrazione assente di una chiamata di Close o a mano; «Si registra» è quella di una chiamata in corso (`recordings.ts`); l'audio di
 * Close da copiare o la copia non riuscita hanno le loro parole (INGR-27).
 */
export const RECORDING_LINES = {
  available: { text: "recordings.states.available", tone: "success", icon: "check" },
  processing: { text: "recordings.states.processing", tone: "warning", icon: "clock" },
  live: { text: "recordings.states.live", tone: "accent", icon: "clock" },
  expired: { text: "recordings.states.expired", tone: "neutral", icon: "minus" },
  manual: { text: "recordings.states.manual", tone: "neutral", icon: "minus" },
  error: { text: "recordings.states.error", tone: "danger", icon: "alert" },
  none: { text: "recordings.states.none", tone: "neutral", icon: "minus" },
  close: { text: "recordings.states.close", tone: "neutral", icon: "minus" },
  closeCopying: { text: "recordings.states.closeCopying", tone: "warning", icon: "clock" },
  closeError: { text: "recordings.states.closeError", tone: "danger", icon: "alert" },
  missing: { text: "recordings.states.missing", tone: "neutral", icon: "minus" },
  notAllowed: { text: "recordings.notAllowed", tone: "neutral", icon: "lock" },
} as const satisfies Record<string, { text: MessageKey; tone: Tone; icon: StatusIcon }>;

export type RecordingLine = keyof typeof RECORDING_LINES;

/** Da dove viene la telefonata: l'importazione da Close, l'inserimento a mano, la telefonia di Gioia365. */
export type CallOrigin = "close_import" | "manual" | "telephony";

/**
 * La riga della registrazione di una telefonata arrivata al Lead (finita o in corso): una chiamata in corso dice soltanto «Si
 * registra» quando la registrazione è cominciata; senza registrazione (`null`) non c'era niente da registrare; da ascoltare, ma
 * non da chi guarda (la chiamata di un collega senza il permesso di ascoltarla), il perché al posto di «Ascolta»; assente, con
 * le parole della sua origine.
 */
export function recordingLineOf(input: { live: boolean; recording: { state: RecordingState } | null; canListen: boolean; origin: CallOrigin }): RecordingLine | null {
  const { recording } = input;
  if (input.live) return recording?.state === "processing" ? "live" : null;
  if (recording === null) return "none";
  switch (recording.state) {
    case "available":
      return input.canListen ? "available" : "notAllowed";
    case "missing":
      return input.origin === "close_import" ? "close" : input.origin === "manual" ? "manual" : "missing";
    // L'audio di Close da copiare nell'archivio di Gioia365, o la copia non riuscita (INGR-27).
    case "processing":
      return input.origin === "close_import" ? "closeCopying" : "processing";
    case "error":
      return input.origin === "close_import" ? "closeError" : "error";
    default:
      return recording.state;
  }
}

/**
 * Ogni quanto si rilegge una pagina con una registrazione in preparazione: l'audio arriva qualche minuto dopo la chiamata. L'audio
 * di Close da copiare non conta: la copia può durare giorni, e chi chiama lo toglie.
 */
export const PROCESSING_REFRESH_MS = 15_000;

/**
 * La rilettura da sola di una pagina della Timeline o di «Conversazioni»: ogni 15 secondi finché una delle sue telefonate ha la
 * registrazione in preparazione, poi più nulla. Con la pagina nascosta (un'altra scheda del browser davanti) la cache non rilegge
 * (`refetchIntervalInBackground` resta spento).
 */
export function refreshWhileProcessing(states: Iterable<RecordingState | null | undefined>): number | false {
  for (const state of states) if (state === "processing") return PROCESSING_REFRESH_MS;
  return false;
}

/** Per quanto, dall'inizio di una telefonata della telefonia, un audio «mancante» si aspetta ancora: la registrazione arriva dopo. */
export const MISSING_AUDIO_WAIT_MS = 10 * 60_000;

/**
 * La rilettura da sola della Timeline (rilievo di Codex sulla #90, turno di notte del 10 ottobre 2026): come
 * `refreshWhileProcessing`, e in più finché una telefonata è ancora viva (`liveState` diverso da `ended`) o è una telefonata della
 * telefonia iniziata da meno di `MISSING_AUDIO_WAIT_MS` con l'audio ancora «mancante» (la riga della registrazione non è ancora
 * arrivata). L'audio di Close ancora da copiare non conta (INGR-27). L'ultimo webhook e la registrazione possono arrivare dopo la rilettura di fine chiamata (`refreshAfterCall`).
 */
export function refreshWhileSettling(
  calls: Iterable<{ source?: string | undefined; liveState?: string | undefined; occurredAt: string; recording?: { state: RecordingState } | null | undefined }>,
  now: number = Date.now(),
): number | false {
  for (const call of calls) {
    // L'audio di Close da copiare può aspettare giorni (INGR-27): non fa rileggere la scheda.
    if (call.recording?.state === "processing" && call.source !== "close_import") return PROCESSING_REFRESH_MS;
    if (call.liveState !== undefined && call.liveState !== "ended") return PROCESSING_REFRESH_MS;
    if (call.source === "telephony" && call.recording?.state === "missing" && now - Date.parse(call.occurredAt) < MISSING_AUDIO_WAIT_MS) return PROCESSING_REFRESH_MS;
  }
  return false;
}
